import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn } from '../components/data/DataGrid';
import FinancialRecordFilterPanel from '../components/data/FinancialRecordFilterPanel';
import FinancialSummaryPanel from '../components/data/FinancialSummaryPanel';
import useFinancialRecordStore from '../store/financialRecordStore';
import useVisuAIDataViewerStore from '../store/visuAIDataViewerStore';
import { FinancialRecordFilter } from '../types/data/financialRecord';
import { generateChartHtml } from '../utils/chartGenerationUtils';
import './VisuAIDataViewerPage.css';

const VisuAIDataViewerPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    records, 
    isLoading, 
    error, 
    filter, 
    summary, 
    totalCount, 
    currentPage, 
    pageSize,
    fetchRecords, 
    setFilter,
    resetState
  } = useFinancialRecordStore();

  const {
    prompt,
    isAnalyzing,
    analysisResult,
    analysisError,
    setPrompt,
    analyzeData,
    clearAnalysis
  } = useVisuAIDataViewerStore();

  const [showSummary, setShowSummary] = useState(true);
  const [isChartWindowOpen, setIsChartWindowOpen] = useState(false);
  const chartWindowRef = useRef<Window | null>(null);
  
  // Define read-only columns for the data grid
  const columns: GridColumn[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'dealerCode', headerName: 'Dealer Code', width: 120 },
    { field: 'dealerName', headerName: 'Dealer Name', width: 200 },
    { field: 'year', headerName: 'Year', width: 100 },
    { field: 'month', headerName: 'Month', width: 100 },
    { field: 'brand', headerName: 'Brand', width: 120 },
    { field: 'groupName', headerName: 'Dealer Group', width: 150 },
    { field: 'newVehicleUnitsSold', headerName: 'New Vehicle Units Sold', width: 180, type: 'number' },
    { field: 'newVehicleSalesRevenue', headerName: 'New Vehicle Sales Revenue', width: 220, type: 'currency' },
    { field: 'newVehicleGrossProfit', headerName: 'New Vehicle Gross Profit', width: 220, type: 'currency' },
    { field: 'usedVehicleUnitsSold', headerName: 'Used Vehicle Units Sold', width: 180, type: 'number' },
    { field: 'usedVehicleSalesRevenue', headerName: 'Used Vehicle Sales Revenue', width: 220, type: 'currency' },
    { field: 'usedVehicleGrossProfit', headerName: 'Used Vehicle Gross Profit', width: 220, type: 'currency' },
    { field: 'financeInsuranceIncome', headerName: 'F&I Income', width: 160, type: 'currency' },
    { field: 'partsServiceRevenue', headerName: 'Parts & Service Revenue', width: 220, type: 'currency' },
    { field: 'partsServiceGrossProfit', headerName: 'Parts & Service Gross Profit', width: 220, type: 'currency' },
    { field: 'totalDirectExpenses', headerName: 'Total Direct Expenses', width: 180, type: 'currency' },
    { field: 'personnelExpenses', headerName: 'Personnel Expenses', width: 180, type: 'currency' },
    { field: 'advertisingExpenses', headerName: 'Advertising Expenses', width: 180, type: 'currency' },
    { field: 'totalDirectProfit', headerName: 'Total Direct Profit', width: 180, type: 'currency' },
    { field: 'newVehiclesDiscountAmount', headerName: 'New Vehicles Discount Amount', width: 220, type: 'currency' },
    { field: 'newVehiclesFinancePenetration', headerName: 'New Vehicles Finance Penetration', width: 220, type: 'number' },
    { field: 'serviceLabourSalesCostofSales', headerName: 'Service Labour Sales Cost of Sales', width: 240, type: 'currency' },
    { field: 'serviceLabourSalesHoursSold', headerName: 'Service Labour Sales Hours Sold', width: 220, type: 'number' },
    { field: 'serviceLabourSalesNetSales', headerName: 'Service Labour Sales Net Sales', width: 220, type: 'currency' },
    { field: 'usedVehiclesOtherMakesUnitsSold', headerName: 'Used Vehicles Other Makes Units Sold', width: 240, type: 'number' },
    { 
      field: 'updatedAt', 
      headerName: 'Last Updated', 
      width: 160, 
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleString();
      }
    }
  ];
  
  // Fetch records on component mount
  useEffect(() => {
    fetchRecords();
    
    // Clean up when component unmounts
    return () => {
      resetState();
      clearAnalysis();
      
      // Close chart window if open
      if (chartWindowRef.current && !chartWindowRef.current.closed) {
        chartWindowRef.current.close();
      }
    };
  }, [fetchRecords, resetState, clearAnalysis]);

  // Effect to generate and display chart when analysis result is received
  useEffect(() => {
    if (analysisResult && !isAnalyzing && records.length > 0) {
      generateAndDisplayChart();
    }
  }, [analysisResult, isAnalyzing]);
  
  // Handle pagination
  const handlePageChange = (newPage: number) => {
    fetchRecords(newPage, pageSize);
  };
  
  // Handle filter application
  const handleApplyFilter = (newFilter: FinancialRecordFilter) => {
    // Set the filter first
    setFilter(newFilter);
    // Then fetch records with the new filter
    fetchRecords(1, pageSize, newFilter);
    // Clear any existing analysis when filter changes
    clearAnalysis();
  };
  
  // Handle filter clearing
  const handleClearFilter = () => {
    // Clear the filter
    setFilter(null);
    // Fetch records without a filter
    fetchRecords(1, pageSize, null);
    // Clear any existing analysis when filter is cleared
    clearAnalysis();
  };

  // Handle prompt submission
  const handlePromptSubmit = () => {
    analyzeData(records);
  };

  // Generate chart and display in new window
  const generateAndDisplayChart = () => {
    if (!analysisResult || records.length === 0) return;
    
    try {
      // Generate the HTML with embedded Chart.js
      const html = generateChartHtml(records, analysisResult);

      // Create new window or reuse existing
      if (chartWindowRef.current && !chartWindowRef.current.closed) {
        chartWindowRef.current.document.open();
        chartWindowRef.current.document.write(html);
        chartWindowRef.current.document.close();
        chartWindowRef.current.focus();
      } else {
        // Open a new window
        const newWindow = window.open('', 'VisuAI_Chart', 'width=1200,height=800');
        if (newWindow) {
          newWindow.document.open();
          newWindow.document.write(html);
          newWindow.document.close();
          chartWindowRef.current = newWindow;
          setIsChartWindowOpen(true);
          
          // Listen for window close
          newWindow.addEventListener('beforeunload', () => {
            setIsChartWindowOpen(false);
          });
        }
      }
    } catch (error) {
      console.error('Error generating or displaying chart:', error);
    }
  };
  
  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);
  
  return (
    <div className="visuai-data-viewer-page">
      <Sidebar />
      
      <div className="visuai-data-viewer-content">
        <div className="page-header">
          <h1>VisuAI Data Viewer</h1>
          <div className="header-actions">
            <button
              className="btn-toggle-summary"
              onClick={() => setShowSummary(!showSummary)}
            >
              {showSummary ? 'Hide Summary' : 'Show Summary'}
            </button>
            {isChartWindowOpen && (
              <button 
                className="btn-view-chart"
                onClick={() => chartWindowRef.current?.focus()}
              >
                Focus Chart Window
              </button>
            )}
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            Error: {error}
          </div>
        )}
        
        <div className="data-filter-section">
          <FinancialRecordFilterPanel
            initialFilter={filter}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilter}
          />
          {isLoading && filter && (
            <div className="filter-loading">
              <div className="spinner-small"></div>
              <span>Applying filters...</span>
            </div>
          )}
        </div>
        
        {showSummary && summary && (
          <div className="data-summary-section">
            <FinancialSummaryPanel summary={summary} />
          </div>
        )}
        
        <div className="data-grid-section">
          <h2>Financial Records</h2>
          
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading financial records...</p>
            </div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">👁️</div>
              <h3>No Financial Records Found</h3>
              <p>To get started, you can:</p>
              <ul>
                <li>Use the <strong>Upload CSV</strong> option in the sidebar to import data</li>
                <li>Add records through the <strong>Financial Data</strong> page</li>
              </ul>
              <button
                className="btn-upload-csv empty-state-button"
                onClick={() => navigate('/upload-csv')}
              >
                Upload CSV
              </button>
            </div>
          ) : (
            <>
              <div className="data-grid-container">
                <DataGrid 
                  columns={columns}
                  rows={records}
                  loading={isLoading}
                  height={500}
                />
              </div>
              
              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="pagination-page">{currentPage} of {totalPages}</span>
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="visuai-prompt-section">
          <h2>Generate Data Visualization</h2>
          <p>Enter a request describing what data you want to visualize:</p>
          <div className="prompt-input-container">
            <textarea 
              className="prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Example: Create a bar chart showing total sales by dealership with new and used vehicle sales"
              rows={3}
            />
            <button 
              className="prompt-submit-button"
              onClick={handlePromptSubmit}
              disabled={!prompt.trim() || records.length === 0 || isAnalyzing}
            >
              {isAnalyzing ? 'Generating...' : 'Generate Visualization'}
            </button>
          </div>
          
          {analysisError && (
            <div className="analysis-error">
              <h3>Error</h3>
              <p>{analysisError}</p>
            </div>
          )}
          
          {isAnalyzing && (
            <div className="analysis-loading">
              <div className="spinner"></div>
              <p>Generating visualization...</p>
            </div>
          )}
          
          {analysisResult && !isAnalyzing && (
            <div className="analysis-result">
              <h3>Visualization Generated</h3>
              <p>The visualization has been generated in a new window. If you don't see it, please check your browser's popup blocker settings.</p>
              <button 
                className="view-chart-button"
                onClick={generateAndDisplayChart}
              >
                {isChartWindowOpen ? 'Regenerate Chart' : 'Open Chart Window'}
              </button>
              <button 
                className="clear-analysis-button"
                onClick={clearAnalysis}
              >
                Clear & Start New
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisuAIDataViewerPage; 